import { DayPicker, getDefaultClassNames, type DayPickerProps } from 'react-day-picker'
import { CaretLeft, CaretRight } from '@/components/icons'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/cn'

export type CalendarProps = DayPickerProps

/**
 * shadcn Calendar on react-day-picker v9. Range selection paints the span in
 * the DS `--secondary` wash with `--primary` end caps, so the picker reads as
 * the same family as every other interactive control.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const defaults = getDefaultClassNames()
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-1', className)}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaults.months),
        month: cn('flex w-full flex-col gap-3', defaults.month),
        nav: cn('absolute inset-x-0 top-0 flex w-full items-center justify-between', defaults.nav),
        button_previous: cn(
          buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
          'size-7 max-md:size-7 p-0 text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]',
          defaults.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
          'size-7 max-md:size-7 p-0 text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]',
          defaults.button_next,
        ),
        month_caption: cn('flex h-7 items-center justify-center px-8', defaults.month_caption),
        caption_label: cn('text-[13px] font-semibold', defaults.caption_label),
        month_grid: cn('w-full border-collapse', defaults.month_grid),
        weekdays: cn('flex', defaults.weekdays),
        weekday: cn(
          'w-8 flex-1 select-none text-center text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]',
          defaults.weekday,
        ),
        week: cn('mt-1 flex w-full', defaults.week),
        day: cn(
          'group/day relative aspect-square h-8 w-8 select-none p-0 text-center text-[12.5px]',
          '[&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaults.day,
        ),
        day_button: cn(
          'flex size-8 items-center justify-center rounded-md tabular-nums leading-none',
          'hover:bg-[color:var(--muted)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--ring)]',
          'group-data-[selected=true]/day:hover:bg-transparent',
          defaults.day_button,
        ),
        range_start: cn(
          'rounded-l-md bg-[color:var(--secondary)] [&>button]:bg-[color:var(--primary)] [&>button]:text-[color:var(--primary-foreground)]',
          defaults.range_start,
        ),
        range_middle: cn(
          'rounded-none bg-[color:var(--secondary)] text-[color:var(--secondary-foreground)]',
          defaults.range_middle,
        ),
        range_end: cn(
          'rounded-r-md bg-[color:var(--secondary)] [&>button]:bg-[color:var(--primary)] [&>button]:text-[color:var(--primary-foreground)]',
          defaults.range_end,
        ),
        selected: cn(
          'data-[range-middle=false]:[&>button]:bg-[color:var(--primary)] data-[range-middle=false]:[&>button]:text-[color:var(--primary-foreground)]',
          defaults.selected,
        ),
        today: cn(
          '[&>button]:font-bold [&>button]:text-[color:var(--primary)] data-[selected=true]:[&>button]:text-[color:var(--primary-foreground)]',
          defaults.today,
        ),
        outside: cn('text-[color:var(--muted-foreground)]/50', defaults.outside),
        disabled: cn(
          'text-[color:var(--muted-foreground)]/40 [&>button]:hover:bg-transparent',
          defaults.disabled,
        ),
        hidden: cn('invisible', defaults.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClass }) =>
          orientation === 'left' ? (
            <CaretLeft className={cn('size-3.5', chevronClass)} />
          ) : (
            <CaretRight className={cn('size-3.5', chevronClass)} />
          ),
      }}
      {...props}
    />
  )
}
